import { useMemo } from "react";
import {
  flexRender,
  getCoreRowModel,
  OnChangeFn,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
import { generatePath, useNavigate } from "react-router-dom";
import { twMerge } from "tailwind-merge";

import {
  EmptyTableState,
  Table,
  TableData,
  TableHead,
  TableHeader,
} from "~/common/components";
import { SortableTableHeader } from "~/providers/components/patientsTable/SortableTableHeader";
import { useUpdatePatientZoomMeetingSessionType } from "~/providers/hooks/queries/useUpdatePatientZoomMeetingSessionType";
import { ROUTES } from "~/providers/router/routes";
import {
  ZoomMeeting,
  ZoomSessionType,
} from "~/providers/utils/types/zoomMeetingTypes";
import {
  getSessionsTableColumns,
  SESSIONS_JOIN_COLUMN_ID,
  SESSIONS_TYPE_COLUMN_ID,
} from "./sessionsTableColumns";

const SESSIONS_TRANSCRIPT_COLUMN_ID = "transcript";
const NON_SORTABLE_SESSIONS_TABLE_COLUMN_IDS = new Set([
  SESSIONS_JOIN_COLUMN_ID,
  SESSIONS_TRANSCRIPT_COLUMN_ID,
]);
const EMPTY_SESSIONS: ZoomMeeting[] = [];

interface SessionsTableProps {
  patientId: string;
  sessions?: ZoomMeeting[];
  isFetching?: boolean;
  sorting: SortingState;
  onSortingChange: OnChangeFn<SortingState>;
}

export const SessionsTable = ({
  patientId,
  sessions,
  isFetching = false,
  sorting,
  onSortingChange,
}: SessionsTableProps) => {
  const navigate = useNavigate();
  const { mutate: updateSessionType, isPending: isUpdatingSessionType } =
    useUpdatePatientZoomMeetingSessionType(patientId);

  const handleSessionTypeChange = (
    sessionId: string,
    sessionType: ZoomSessionType | null,
  ) => {
    updateSessionType({ sessionId, session_type: sessionType });
  };

  const columns = useMemo(
    () =>
      getSessionsTableColumns({
        onSessionTypeChange: handleSessionTypeChange,
        isUpdatingSessionType,
      }),
    [isUpdatingSessionType],
  );

  const tableData = useMemo(() => sessions ?? EMPTY_SESSIONS, [sessions]);
  const noSessions = !tableData.length;

  const table = useReactTable({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true,
    enableSortingRemoval: false,
    onSortingChange,
    state: { sorting },
  });

  return (
    <div
      className={twMerge(
        "w-full overflow-x-auto rounded-xl border border-gray-200",
        noSessions && "h-table",
      )}
    >
      <Table className="size-full table-auto">
        <TableHead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id} className="w-full">
              {headerGroup.headers.map((header) => {
                const isNonSortableColumn =
                  NON_SORTABLE_SESSIONS_TABLE_COLUMN_IDS.has(header.column.id);

                if (isNonSortableColumn || !header.column.getCanSort()) {
                  return (
                    <TableHeader key={header.id} className="flex-1" scope="col">
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                    </TableHeader>
                  );
                }

                return (
                  <SortableTableHeader
                    key={header.id}
                    header={header}
                    className="flex-1"
                  />
                );
              })}
            </tr>
          ))}
        </TableHead>
        <tbody className="bg-white text-sm text-gray-70">
          {noSessions && (
            <EmptyTableState
              colSpan={columns.length}
              message="No sessions found for this patient."
            />
          )}
          {table.getRowModel().rows.map((row) => (
            <tr
              key={row.id}
              className={twMerge(
                "cursor-pointer transition hover:bg-gray-50",
                isFetching && "opacity-75",
              )}
              onClick={() =>
                navigate(
                  generatePath(ROUTES.sessionDetailTranscript, {
                    patientId,
                    sessionId: row.original.id,
                  }),
                )
              }
            >
              {row.getVisibleCells().map((cell) => (
                <TableData
                  key={cell.id}
                  className={
                    cell.column.id === SESSIONS_TYPE_COLUMN_ID
                      ? "overflow-visible"
                      : undefined
                  }
                  onClick={
                    cell.column.id === SESSIONS_TYPE_COLUMN_ID
                      ? (event) => event.stopPropagation()
                      : undefined
                  }
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableData>
              ))}
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
};
